import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  attendanceRulesApi,
  SEARCH_FIELD_OPTIONS,
  STATUS_OPTIONS,
  TOOLS_OPTIONS,
} from '../api/attendanceRulesApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

const REPORT_COLUMNS = [
  { key: 'ruleName', label: 'Rule Name' },
  { key: 'alias', label: 'Alias' },
  { key: 'swipeTimeAfter', label: 'Swipe After (m)' },
  { key: 'swipeTimeBefore', label: 'Swipe Before (m)' },
  { key: 'lateHalfDayDeduct', label: 'Late Half-Day (m)' },
  { key: 'earlyHalfDayDeduct', label: 'Early Half-Day (m)' },
  { key: 'noOfLateEarlyMarks', label: 'Late Marks' },
  { key: 'lateEarlyDaysDeduct', label: 'Late Days Deduct' },
  { key: 'earlyGoHf', label: 'Early Go HF' },
  { key: 'earlyGoDaysDeduct', label: 'Early Days Deduct' },
  { key: 'sandwitchPresent', label: 'Sandwich', render: (val) => (val ? 'Yes' : 'No') },
  { key: 'status', label: 'Status' },
];

export function useAttendanceRulesList() {
  const [searchField, setSearchField] = useState('Rule Name');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Background Export Job via JobEngine
  const { triggerExport, isExporting } = useExportJob({
    module: 'attendance_rules',
    jobType: 'EXPORT',
    fileFormat: 'XLSX',
  });

  const activeFilters = useMemo(
    () => ({
      searchText: searchText?.trim() || undefined,
      searchField: searchField || undefined,
      status: statusFilter,
    }),
    [searchText, searchField, statusFilter]
  );

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await attendanceRulesApi.getRecords({
        searchField,
        searchText,
        status: statusFilter,
      });
      setRecords(data);
    } catch (err) {
      appToast.error('Failed to load Attendance Rules.');
    } finally {
      setIsLoading(false);
    }
  }, [searchField, searchText, statusFilter]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setSearchField('Rule Name');
    setSearchText('');
    setStatusFilter('Active');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const handleArchive = async (id, ruleName, currentStatus) => {
    const isInactive = currentStatus === 'Inactive';
    const actionLabel = isInactive ? 'unarchive' : 'archive';
    const confirmed = await appConfirm({
      title: `${isInactive ? 'Unarchive' : 'Archive'} Attendance Rule`,
      message: `Are you sure you want to ${actionLabel} attendance rule "${ruleName || id}"?`,
      confirmText: isInactive ? 'Unarchive Rule' : 'Archive Rule',
      variant: isInactive ? 'primary' : 'warning',
      icon: isInactive ? 'unarchive' : 'archive',
    });
    if (!confirmed) {
      return;
    }
    try {
      await attendanceRulesApi.archiveRecord(id);
      appToast.success(`Attendance rule "${ruleName}" ${isInactive ? 'unarchived' : 'archived'} successfully.`);
      loadRecords();
    } catch (err) {
      const msg = err.response?.data?.detail;
      appToast.error(typeof msg === 'string' ? msg : `Failed to ${actionLabel} attendance rule.`);
    }
  };

  const handleDelete = async (id, ruleName) => {
    const confirmed = await appConfirm({
      title: 'Delete Attendance Rule',
      message: `Are you sure you want to delete attendance rule "${ruleName || id}"? This action cannot be undone.`,
      confirmText: 'Delete Rule',
      variant: 'danger',
      icon: 'delete',
    });
    if (!confirmed) {
      return;
    }
    try {
      await attendanceRulesApi.deleteRecord(id);
      appToast.success(`Attendance rule "${ruleName || id}" deleted successfully.`);
      loadRecords();
    } catch (err) {
      const msg = err.response?.data?.detail;
      appToast.error(typeof msg === 'string' ? msg : 'Failed to delete attendance rule.');
    }
  };

  const handleToolSelect = async (actionKey) => {
    if (actionKey === 'print') {
      executePrintReport({
        title: 'Attendance Rules Report',
        columns: REPORT_COLUMNS,
        metadata: {
          'Status': statusFilter || 'All',
          'Search': searchText || 'All',
        },
        fetchReportData: async () => {
          const res = await attendanceRulesApi.getAttendanceRulesReport(activeFilters);
          return { items: res || [] };
        },
        onExportPdf: () => triggerExport(activeFilters, 'PDF'),
        onExportExcel: () => triggerExport(activeFilters, 'XLSX'),
        threshold: 1000,
      });
    } else if (actionKey === 'export_excel') {
      await triggerExport(activeFilters, 'XLSX');
    } else if (actionKey === 'export_pdf') {
      await triggerExport(activeFilters, 'PDF');
    }
  };

  const paginatedRecords = records.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(records.length / pageSize) || 1;

  return {
    state: {
      records: paginatedRecords,
      rawRecords: records,
      isLoading,
      isExporting,
      searchField,
      searchText,
      statusFilter,
      page,
      pageSize,
      totalCount: records.length,
      totalPages,
      searchFieldOptions: SEARCH_FIELD_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      tools: TOOLS_OPTIONS,
    },
    actions: {
      setSearchField,
      setSearchText,
      setStatusFilter,
      handleStatusTabChange,
      setPage,
      handleSearch,
      resetFilters,
      handleArchive,
      handleDelete,
      handleToolSelect,
      refresh: loadRecords,
    },
  };
}
